import React, { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Tag, Trash2, Save, Wrench } from "lucide-react";
import { FieldLabel } from "@/pages/admin/FieldLabel";

export function ServicesEditor({ services, categories, onUpdated, onCreated, onDeleted }) {
  const [adding, setAdding] = useState(false);
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end">
        <button
          data-testid="svc-add-toggle"
          onClick={() => setAdding((v) => !v)}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-red text-white text-xs font-mono uppercase tracking-widest hover:bg-[#B91C2B]"
        >
          <Plus className="h-3.5 w-3.5" /> {adding ? "Cancel" : "Add service"}
        </button>
      </div>
      {adding && (
        <AddServiceForm
          categories={categories}
          onCreated={(c) => { onCreated(c); setAdding(false); }}
        />
      )}
      {!services.length ? (
        <div className="text-zinc-500 font-mono text-xs uppercase tracking-widest py-10">No services found.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-zinc-900 border border-zinc-900">
          {services.map((s) => (
            <ServiceEditorRow key={s.id} service={s} categories={categories} onUpdated={onUpdated} onDeleted={onDeleted} />
          ))}
        </div>
      )}
    </div>
  );
}

function AddServiceForm({ categories, onCreated }) {
  const [form, setForm] = useState({ id: "", title: "", description: "", price_from: 100, duration_min: 60, category: "", icon: "Cog" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.id.trim() || !form.title.trim()) {
      toast.error("ID and Title are required");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/services", {
        ...form,
        id: form.id.toLowerCase().replace(/[^a-z0-9_-]/g, "-"),
        price_from: Number(form.price_from),
        duration_min: Number(form.duration_min),
      });
      onCreated(data);
      toast.success(`Added: ${data.title}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to add service");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="svc-add-form" className="border border-zinc-900 bg-zinc-950 p-6 space-y-4">
      <h3 className="font-display uppercase text-xl text-white tracking-tight">New service</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FieldLabel label="Internal ID (lowercase, no spaces)">
          <input data-testid="svc-new-id" required className="ta-svc-input" value={form.id} onChange={(e) => setForm({ ...form, id: e.target.value })} placeholder="suspension" />
        </FieldLabel>
        <FieldLabel label="Title">
          <input data-testid="svc-new-title" required className="ta-svc-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Suspension Service" />
        </FieldLabel>
      </div>
      <FieldLabel label="Description">
        <textarea data-testid="svc-new-desc" required rows={3} className="ta-svc-input resize-none" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What does this service include?" />
      </FieldLabel>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <FieldLabel label="Price from (AUD)">
          <input data-testid="svc-new-price" type="number" min="0" step="1" className="ta-svc-input" value={form.price_from} onChange={(e) => setForm({ ...form, price_from: e.target.value })} />
        </FieldLabel>
        <FieldLabel label="Duration (min)">
          <input data-testid="svc-new-duration" type="number" min="5" step="5" className="ta-svc-input" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} />
        </FieldLabel>
        <FieldLabel label="Category">
          <select data-testid="svc-new-category" className="ta-svc-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
            <option value="">— Uncategorised —</option>
            {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
          </select>
        </FieldLabel>
      </div>
      <button data-testid="svc-new-submit" type="submit" disabled={saving} className="inline-flex items-center gap-2 px-6 py-3 bg-brand-red text-white text-xs font-mono uppercase tracking-widest hover:bg-[#B91C2B] disabled:opacity-50">
        <Plus className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Create service"}
      </button>
    </form>
  );
}

export function CategoriesManager({ categories, onCreated, onDeleted }) {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post("/admin/categories", { name: name.trim() });
      onCreated(data);
      setName("");
      toast.success(`Added category: ${data.name}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to add category");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id, label) => {
    if (!window.confirm(`Delete category "${label}"?`)) return;
    try {
      await api.delete(`/admin/categories/${id}`);
      onDeleted(id);
      toast.success("Category deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="space-y-6">
      <form onSubmit={add} data-testid="cat-add-form" className="border border-zinc-900 bg-zinc-950 p-6 flex flex-col sm:flex-row gap-3 items-stretch sm:items-end">
        <div className="flex-1">
          <FieldLabel label="New category name">
            <input data-testid="cat-name" className="ta-svc-input" placeholder="e.g. Mechanical, Electrical, Tyres" value={name} onChange={(e) => setName(e.target.value)} />
          </FieldLabel>
        </div>
        <button data-testid="cat-submit" disabled={saving || !name.trim()} className="px-6 py-3 bg-brand-red text-white text-xs font-mono uppercase tracking-widest hover:bg-[#B91C2B] disabled:opacity-50 inline-flex items-center gap-2">
          <Plus className="h-3.5 w-3.5" /> {saving ? "Adding…" : "Add"}
        </button>
      </form>

      {!categories.length ? (
        <div className="text-zinc-500 font-mono text-xs uppercase tracking-widest py-6 px-1">No categories yet — add your first one above.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900">
          {categories.map((c) => (
            <div key={c.id} data-testid={`cat-${c.id}`} className="bg-zinc-950 p-5 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Tag className="h-4 w-4 text-brand-red" />
                <span className="font-display uppercase tracking-tight text-white">{c.name}</span>
              </div>
              <button data-testid={`cat-del-${c.id}`} onClick={() => remove(c.id, c.name)} className="text-zinc-500 hover:text-brand-red" aria-label="Delete">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      <style>{`
        .ta-svc-input { width:100%; background:#0a0a0a; border:1px solid #27272a; color:#fafafa; padding:10px 12px; font-family:'IBM Plex Sans',sans-serif; font-size:14px; outline:none; transition:border-color .15s; }
        .ta-svc-input:focus { border-color:#E11D2B; box-shadow:0 0 0 1px #E11D2B; }
      `}</style>
    </div>
  );
}

function ServiceEditorRow({ service, categories, onUpdated, onDeleted }) {
  const [title, setTitle] = useState(service.title);
  const [description, setDescription] = useState(service.description);
  const [priceFrom, setPriceFrom] = useState(service.price_from);
  const [durationMin, setDurationMin] = useState(service.duration_min);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const remove = async () => {
    if (!window.confirm(`Delete service "${service.title}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/services/${service.id}`);
      onDeleted(service.id);
      toast.success(`${service.title} deleted`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
      setDeleting(false);
    }
  };

  const dirty =
    title !== service.title ||
    description !== service.description ||
    Number(priceFrom) !== Number(service.price_from) ||
    Number(durationMin) !== Number(service.duration_min);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/services/${service.id}`, {
        title,
        description,
        price_from: Number(priceFrom),
        duration_min: Number(durationMin),
      });
      onUpdated(data);
      toast.success(`${data.title} updated`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    setTitle(service.title);
    setDescription(service.description);
    setPriceFrom(service.price_from);
    setDurationMin(service.duration_min);
  };

  return (
    <article data-testid={`svc-edit-${service.id}`} className="bg-zinc-950 p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 border border-zinc-800 grid place-items-center">
            <Wrench className="h-4 w-4 text-brand-red" />
          </div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{service.id}</div>
        </div>
        {dirty && <span className="text-[10px] font-mono uppercase tracking-widest text-yellow-400">Unsaved</span>}
      </div>

      <FieldLabel label="Title">
        <input
          data-testid={`svc-title-${service.id}`}
          className="ta-svc-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </FieldLabel>

      <FieldLabel label="Description">
        <textarea
          data-testid={`svc-desc-${service.id}`}
          rows={3}
          className="ta-svc-input resize-none"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </FieldLabel>

      <div className="grid grid-cols-2 gap-4">
        <FieldLabel label="Price from (AUD)">
          <input
            data-testid={`svc-price-${service.id}`}
            type="number"
            min="0"
            step="1"
            className="ta-svc-input"
            value={priceFrom}
            onChange={(e) => setPriceFrom(e.target.value)}
          />
        </FieldLabel>
        <FieldLabel label="Duration (minutes)">
          <input
            data-testid={`svc-duration-${service.id}`}
            type="number"
            min="5"
            step="5"
            className="ta-svc-input"
            value={durationMin}
            onChange={(e) => setDurationMin(e.target.value)}
          />
        </FieldLabel>
      </div>

      <div className="flex items-center gap-2 pt-2">
        <button
          data-testid={`svc-save-${service.id}`}
          onClick={save}
          disabled={!dirty || saving}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-red text-white text-xs font-mono uppercase tracking-widest hover:bg-[#B91C2B] disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save changes"}
        </button>
        {dirty && (
          <button
            data-testid={`svc-reset-${service.id}`}
            onClick={reset}
            className="px-4 py-2.5 border border-zinc-800 text-zinc-300 text-xs font-mono uppercase tracking-widest hover:border-brand-red hover:text-brand-red"
          >
            Reset
          </button>
        )}
        <div className="ml-auto" />
        <button
          data-testid={`svc-del-${service.id}`}
          onClick={remove}
          disabled={deleting}
          title="Delete service"
          className="inline-flex items-center gap-2 px-3 py-2.5 border border-zinc-800 text-zinc-400 text-xs font-mono uppercase tracking-widest hover:border-red-600 hover:text-red-400 disabled:opacity-40"
        >
          <Trash2 className="h-3.5 w-3.5" /> {deleting ? "…" : "Delete"}
        </button>
      </div>

      <style>{`
        .ta-svc-input {
          width: 100%;
          background: #0a0a0a;
          border: 1px solid #27272a;
          color: #fafafa;
          padding: 10px 12px;
          font-family: 'IBM Plex Sans', sans-serif;
          font-size: 14px;
          outline: none;
          transition: border-color 0.15s;
        }
        .ta-svc-input:focus { border-color: #E11D2B; box-shadow: 0 0 0 1px #E11D2B; }
      `}</style>
    </article>
  );
}

